<template>
    <component
        :is="tag"
        :aria-busy="loading"
        :aria-describedby="ariaDescribedby"
        :aria-label="computedAriaLabel"
        :class="buttonClasses"
        :disabled="disabled || loading"
        :href="linkRoute"
        :target="link ? '_blank' : undefined"
        :type="tag === 'button' ? nativeType : undefined"
        @click="onClick"
    >
        <f-a-icon v-if="loading" class="size-3.5 animate-spin" name="spinner-third" />
        <!--
            FontAwesomeIcon sets `height: 1em` as an inline style on the rendered SVG,
            which overrides Tailwind size utilities. The wrapper span defines the intended
            icon size, and `[&>svg]:size-full!` forces the SVG to fill it — keeping the
            !important concern contained here rather than leaking it to consumers.
        -->
        <span v-else-if="$slots['icon-left']" class="size-5 shrink-0 [&>svg]:size-full!">
            <slot name="icon-left" />
        </span>
        <span v-if="label" v-text="label" />
        <span v-if="$slots['icon-right']" class="size-5 shrink-0 [&>svg]:size-full!">
            <slot name="icon-right" />
        </span>
    </component>
</template>

<script setup lang="ts">
import {computed, toRef} from 'vue';
import {type RouterLinkProps} from 'vue-router';
import useButtonLink from '../../../composables/useButtonLink';
import FAIcon from '../../icons/FAIcon.vue';

type Props = {
    variant?:
        | 'primary'
        | 'secondary'
        | 'tertiary'
        | 'textlink'
        | 'primary-danger'
        | 'tertiary-danger';
    label?: string | null;
    disabled?: boolean;
    nativeType?: 'button' | 'submit';
    to?: RouterLinkProps['to'] | null;
    link?: string;
    ariaLabel?: string;
    ariaDescribedby?: string;
    loading?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
    variant: 'primary',
    label: null,
    nativeType: 'button',
    to: null,
    link: undefined,
    ariaLabel: undefined,
    ariaDescribedby: undefined,
});

const {route, onClick, as} = useButtonLink(toRef(() => props.to));

const tag = computed(() => (props.link ? 'a' : as.value || 'button'));

const linkRoute = computed(() => route.value?.href ?? props.link);

const computedAriaLabel = computed(() => props.ariaLabel || props.label || undefined);

const baseClasses =
    'inline-flex items-center justify-center gap-2 font-deskly font-semibold text-button rounded-full select-none transition-colors duration-200 ease-in-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus:outline-none';

const variantClasses: Record<NonNullable<Props['variant']>, string> = {
    primary:
        'bg-primary text-primary-inverse hover:bg-primary-hover-contained-button active:bg-primary-pressed-contained-button',
    secondary:
        'border border-primary text-primary hover:border-transparent hover:bg-primary-hover-outlined-button active:bg-primary-pressed-outlined-button',
    tertiary: 'text-tertiary hover:text-tertiary-hover active:text-tertiary-pressed',
    textlink: 'text-tertiary hover:text-tertiary-hover active:text-tertiary-pressed',
    'primary-danger':
        'bg-primary-danger text-white hover:bg-primary-danger-hover active:bg-primary-danger-pressed',
    'tertiary-danger':
        'text-primary-danger hover:text-primary-danger-hover active:text-primary-danger-pressed',
};

const buttonClasses = computed(() => [
    baseClasses,
    props.variant === 'textlink' ? 'h-fit' : 'h-11 px-6', // Default 44px height
    variantClasses[props.variant],
    props.disabled || props.loading
        ? 'opacity-40 pointer-events-none cursor-default'
        : 'cursor-pointer',
]);
</script>
